import React from 'react';
import { alpha, keyframes } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ElectricCarRoundedIcon from '@mui/icons-material/ElectricCarRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import AppTheme from './AppTheme';
import { CustomIcon } from './AppNavbar';

const DEMO_CREDENTIALS = {
    userId: 'evadmin',
    password: 'EV@Dashboard2026',
};

export default function LoginPage() {
    const navigate = useNavigate();
    const { isAuthenticated, login } = useAuth();
    const [credentials, setCredentials] = React.useState({ userId: '', password: '' });
    const [error, setError] = React.useState('');

    if (isAuthenticated) {
        return <Navigate to="/dashboard" replace />;
    }

    const handleChange = (field) => (event) => {
        setError('');
        setCredentials((current) => ({
            ...current,
            [field]: event.target.value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (
            credentials.userId.trim() === DEMO_CREDENTIALS.userId &&
            credentials.password === DEMO_CREDENTIALS.password
        ) {
            login(credentials.userId.trim());
            navigate('/dashboard', { replace: true });
            return;
        }

        setError('Invalid login ID or password. Use the demo credentials shown below.');
    };

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <Box
                sx={(theme) => ({
                    minHeight: '100vh',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 5 },
                    background: theme.vars
                        ? `radial-gradient(circle at 12% 18%, rgba(${theme.vars.palette.primary.mainChannel} / 0.24), transparent 20%), radial-gradient(circle at 88% 24%, rgba(${theme.vars.palette.info.mainChannel} / 0.18), transparent 18%), linear-gradient(135deg, rgba(${theme.vars.palette.background.defaultChannel} / 1) 0%, rgba(${theme.vars.palette.background.paperChannel} / 1) 100%)`
                        : `radial-gradient(circle at 12% 18%, ${alpha(theme.palette.primary.main, 0.24)}, transparent 20%), radial-gradient(circle at 88% 24%, ${alpha(theme.palette.info.main, 0.18)}, transparent 18%), linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                })}
            >
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: theme.vars
                            ? `linear-gradient(rgba(${theme.vars.palette.common.whiteChannel} / 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(${theme.vars.palette.common.whiteChannel} / 0.04) 1px, transparent 1px)`
                            : `linear-gradient(${alpha(theme.palette.common.white, 0.04)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.palette.common.white, 0.04)} 1px, transparent 1px)`,
                        backgroundSize: '44px 44px',
                        maskImage: 'linear-gradient(180deg, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.2))',
                        pointerEvents: 'none',
                    })}
                />
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        width: 420,
                        height: 420,
                        borderRadius: '50%',
                        top: '-10%',
                        right: '-8%',
                        filter: 'blur(72px)',
                        background: alpha(theme.palette.primary.main, 0.18),
                        pointerEvents: 'none',
                    })}
                />
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        width: 320,
                        height: 320,
                        borderRadius: '50%',
                        bottom: '-8%',
                        left: '-6%',
                        filter: 'blur(72px)',
                        background: alpha(theme.palette.info.main, 0.16),
                        pointerEvents: 'none',
                    })}
                />
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        width: '100%',
                        maxWidth: 1080,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        backdropFilter: 'blur(22px)',
                        boxShadow: `0 32px 80px ${alpha(theme.palette.common.black, 0.3)}`,
                        background: theme.vars
                            ? `linear-gradient(145deg, rgba(${theme.vars.palette.background.paperChannel} / 0.68) 0%, rgba(${theme.vars.palette.background.defaultChannel} / 0.84) 100%)`
                            : `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.68)} 0%, ${alpha(theme.palette.background.default, 0.84)} 100%)`,
                    })}
                >
                    <Stack direction={{ xs: 'column', md: 'row' }}>
                        <Box
                            sx={(theme) => ({
                                position: 'relative',
                                flex: { xs: '1 1 auto', md: '0 0 47%' },
                                p: { xs: 3.5, md: 5 },
                                borderRight: { md: '1px solid' },
                                borderColor: 'divider',
                                background: theme.vars
                                    ? `linear-gradient(180deg, rgba(${theme.vars.palette.primary.mainChannel} / 0.08) 0%, rgba(${theme.vars.palette.background.paperChannel} / 0.02) 100%)`
                                    : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.background.paper, 0.02)} 100%)`,
                            })}
                        >
                            <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                    <CustomIcon />
                                    <Chip
                                        size="small"
                                        icon={<BoltRoundedIcon />}
                                        label="Electric Intelligence"
                                        sx={{
                                            borderRadius: 999,
                                            bgcolor: 'transparent',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '& .MuiChip-icon': { color: 'primary.main' },
                                        }}
                                    />
                                </Stack>

                                <Box>
                                    <Typography variant="overline" sx={{ letterSpacing: '0.28em', color: 'primary.main' }}>
                                        Secure Entry
                                    </Typography>
                                    <Typography variant="h3" sx={{ mt: 1.5, maxWidth: 420, lineHeight: 1.15 }}>
                                        Premium access to your EV command center.
                                    </Typography>
                                    <Typography variant="body1" sx={{ mt: 2, maxWidth: 460, color: 'text.secondary' }}>
                                        Monitor adoption trends, manufacturer leadership, battery range behavior, and regional EV momentum from one dashboard designed for executive-level clarity.
                                    </Typography>
                                </Box>

                                <Stack spacing={1.5}>
                                    <Paper
                                        variant="outlined"
                                        sx={(theme) => ({
                                            p: 2,
                                            borderRadius: 4,
                                            bgcolor: alpha(theme.palette.background.paper, 0.18),
                                            backdropFilter: 'blur(14px)',
                                        })}
                                    >
                                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                                            <ElectricCarRoundedIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle2">Live EV analytics</Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                    Optimized CSV ingestion, cache-backed loading, and cleaner dashboard entry flow.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    <Paper
                                        variant="outlined"
                                        sx={(theme) => ({
                                            p: 2,
                                            borderRadius: 4,
                                            bgcolor: alpha(theme.palette.background.paper, 0.14),
                                            backdropFilter: 'blur(14px)',
                                        })}
                                    >
                                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                                            <ShieldRoundedIcon color="primary" />
                                            <Box>
                                                <Typography variant="subtitle2">Protected entry</Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                    Use the demo credentials from the field hints to enter the dashboard quickly during review.
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Stack>
                        </Box>

                        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, p: { xs: 3.5, md: 5 } }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h4">Sign In</Typography>
                                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                        Continue to the EV dashboard workspace.
                                    </Typography>
                                </Box>

                                {error ? (
                                    <Alert
                                        severity="error"
                                        sx={(theme) => ({
                                            borderRadius: 3,
                                            border: '1px solid',
                                            borderColor: alpha(theme.palette.error.main, 0.22),
                                        })}
                                    >
                                        {error}
                                    </Alert>
                                ) : null}

                                <Stack spacing={2.25}>
                                    <Box>
                                        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: 'text.secondary' }}>Login ID *</Typography>
                                        <TextField
                                            value={credentials.userId}
                                            onChange={handleChange('userId')}
                                            autoComplete="username"
                                            fullWidth
                                            required
                                            placeholder={DEMO_CREDENTIALS.userId}
                                            helperText={`Demo login ID: ${DEMO_CREDENTIALS.userId}`}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PersonRoundedIcon sx={{ color: 'text.secondary' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 500, color: 'text.secondary' }}>Password *</Typography>
                                        <TextField
                                            type="password"
                                            value={credentials.password}
                                            onChange={handleChange('password')}
                                            autoComplete="current-password"
                                            fullWidth
                                            required
                                            placeholder={DEMO_CREDENTIALS.password}
                                            helperText={`Demo password: ${DEMO_CREDENTIALS.password}`}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <KeyRoundedIcon sx={{ color: 'text.secondary' }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Stack>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    endIcon={<BoltRoundedIcon />}
                                    sx={{
                                        py: 1.45,
                                        borderRadius: 999,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        border: '1px solid rgba(157, 182, 255, 0.52)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        isolation: 'isolate',
                                        animation: `${keyframes`
                                            0%   { background-position: 0% 50%; }
                                            50%  { background-position: 100% 50%; }
                                            100% { background-position: 0% 50%; }
                                        `} 3s ease infinite`,
                                        transition: 'transform 0.18s ease, filter 0.18s ease, box-shadow 0.18s ease',
                                        '&&': {
                                            backgroundColor: '#1f2f7a',
                                            backgroundImage: 'linear-gradient(105deg, #2f4cb7 0%, #223b96 35%, #1f2f7a 65%, #17265f 100%)',
                                            backgroundSize: '220% 100%',
                                            color: '#dfe7ff',
                                            boxShadow: '0 10px 34px rgba(27, 42, 107, 0.52)',
                                        },
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: '-140%',
                                            width: '120%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, transparent 0%, rgba(221, 232, 255, 0.44) 50%, transparent 100%)',
                                            transform: 'skewX(-22deg)',
                                            transition: 'left 0.45s ease',
                                            zIndex: 0,
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '0%',
                                            height: '100%',
                                            background: 'linear-gradient(90deg, rgba(58, 93, 215, 0.22) 0%, rgba(42, 72, 174, 0.34) 100%)',
                                            transition: 'width 0.35s ease',
                                            zIndex: 0,
                                        },
                                        '& .submitLabel': {
                                            position: 'relative',
                                            zIndex: 1,
                                            background: 'linear-gradient(90deg, #f8fbff 0%, #c7d8ff 45%, #a8c1ff 100%)',
                                            WebkitBackgroundClip: 'text',
                                            backgroundClip: 'text',
                                            color: 'transparent',
                                            WebkitTextFillColor: 'transparent',
                                        },
                                        '& .MuiButton-endIcon, & .MuiButton-endIcon svg': {
                                            position: 'relative',
                                            zIndex: 1,
                                            color: '#d8e4ff',
                                        },
                                        '&&:hover': {
                                            backgroundColor: '#253b96',
                                            backgroundImage: 'linear-gradient(105deg, #3b62dd 0%, #2b47b2 40%, #253b96 70%, #1a2c73 100%)',
                                            backgroundSize: '220% 100%',
                                            transform: 'translateY(-2px) scale(1.025)',
                                            filter: 'brightness(1.08)',
                                            boxShadow: '0 16px 46px rgba(33, 58, 150, 0.58)',
                                        },
                                        '&:hover::before': {
                                            left: '130%',
                                        },
                                        '&:hover::after': {
                                            width: '100%',
                                        },
                                        '&:focus-visible::after': {
                                            width: '100%',
                                        },
                                        '&:active': {
                                            transform: 'translateY(0px) scale(0.985)',
                                        },
                                    }}
                                >
                                    <Box component="span" className="submitLabel">Enter Dashboard</Box>
                                </Button>

                                <Divider />

                                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                                    <ShieldRoundedIcon sx={{ color: 'primary.main' }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Demo-only authentication for portfolio review.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>
            </Box>
        </AppTheme>
    );
}